import React, { useState } from 'react';
import { Action } from '@reduxjs/toolkit';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, Divider, Menu, MenuItem } from '@mui/material';

import {
  hasAnalysisEvaluationPermission,
  hasBladeDownloadsPermission,
  hasCybDownloadsPermission,
  hasSteelTrsProfilePermission,
  hasTrsDownloadsPermission,
  hasTrussDesignPermission,
} from '@paragon/login-tools';

import {
  showMaterialDetailsOptionsDialog,
  showQuantitiesDialog,
  showShopDrawingOptionsDialog,
  showTrussDesignDrawingDownloadDialog,
} from '../../store/actions/dialogActions';
import { downloadComprehensiveReports, downloadEngineeringReport } from '../../store/actions/download';
import { ThunkActionVoid } from '../../store/actions/thunkActions';
import { ProjectActionScope } from '../../store/projectActionScope';
import { canDownloadComponentSummary } from '../../store/selectors/downloadPermissions';
import { useAppDispatch, useAppSelector } from '../hooks';

interface Props {
  readonly scope: ProjectActionScope;
}

export function DownloadButtons(props: Props) {
  const dispatch = useAppDispatch();
  const componentSummaryDownloadsAllowed = useAppSelector(canDownloadComponentSummary);
  const canDownloadTrussDesignDrawings = useAppSelector(hasTrussDesignPermission);
  const hasAnalysisEvaluationAccess = useAppSelector(hasAnalysisEvaluationPermission);

  const hasBladeFeatureFlag = useAppSelector(hasBladeDownloadsPermission);
  const hasCybFeatureFlag = useAppSelector(hasCybDownloadsPermission);
  const hasTrsFeatureFlag = useAppSelector(hasTrsDownloadsPermission);
  const hasSteelTrsFeatureFlag = useAppSelector(hasSteelTrsProfilePermission);

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const menuIsOpen = !!menuAnchorEl;

  const closeAndDispatch = (action: Action | ThunkActionVoid) => () => {
    setMenuAnchorEl(null);
    dispatch(action);
  };

  return (
    <>
      <Button size="small" onClick={(event) => setMenuAnchorEl(event.currentTarget)} endIcon={<ArrowDropDownIcon />}>
        Download
      </Button>
      <Menu open={menuIsOpen} anchorEl={menuAnchorEl} onClose={() => setMenuAnchorEl(null)}>
        {componentSummaryDownloadsAllowed && (
          <MenuItem onClick={closeAndDispatch(showQuantitiesDialog('DownloadComponentReport', props.scope))}>
            Component Report
          </MenuItem>
        )}
        {hasAnalysisEvaluationAccess ? (
          <MenuItem onClick={closeAndDispatch(downloadComprehensiveReports(props.scope))} key="comprehensive-reports">
            Comprehensive Reports
          </MenuItem>
        ) : null}
        <MenuItem onClick={closeAndDispatch(downloadEngineeringReport(props.scope))} key="engineering-report">
          Engineering Report
        </MenuItem>
        <MenuItem onClick={closeAndDispatch(showMaterialDetailsOptionsDialog(props.scope))}>Material Report</MenuItem>
        <MenuItem
          key="download-lumber-pick-list"
          onClick={closeAndDispatch(showQuantitiesDialog('DownloadLumberPickList', props.scope))}
        >
          Lumber Pick List
        </MenuItem>
        <MenuItem
          key="download-plate-pick-list"
          onClick={closeAndDispatch(showQuantitiesDialog('DownloadPlatesPickList', props.scope))}
        >
          Plate Pick List
        </MenuItem>
        <Divider />
        <MenuItem key="download-shop-drawings" onClick={closeAndDispatch(showShopDrawingOptionsDialog(props.scope))}>
          Shop Drawings
        </MenuItem>
        {canDownloadTrussDesignDrawings ? (
          <MenuItem
            key="download-truss-design-drawings"
            onClick={closeAndDispatch(showTrussDesignDrawingDownloadDialog(props.scope))}
          >
            Truss Design Drawings
          </MenuItem>
        ) : null}
        <Divider key="download-divider" />
        <MenuItem key="download-tps-files" onClick={closeAndDispatch(showQuantitiesDialog('DownloadTps', props.scope))}>
          TPS Files
        </MenuItem>
        <MenuItem
          key="download-tre-files"
          onClick={closeAndDispatch(showQuantitiesDialog('DownloadTres', props.scope))}
        >
          TRE Files
        </MenuItem>
        {hasTrsFeatureFlag ? (
          <MenuItem
            key="download-trs-profile-files"
            onClick={closeAndDispatch(showQuantitiesDialog('DownloadTrs', props.scope))}
          >
            TRS Profile File
          </MenuItem>
        ) : null}
        {hasSteelTrsFeatureFlag ? (
          <MenuItem
            key="download-truss-d-and-e"
            onClick={closeAndDispatch(showQuantitiesDialog('DownloadTrussDAndE', props.scope))}
          >
            Truss D&E
          </MenuItem>
        ) : null}
        <MenuItem
          key="download-trs-machinery-files"
          onClick={closeAndDispatch(showQuantitiesDialog('DownloadTrsMachinery', props.scope))}
        >
          TRS Machinery Files
        </MenuItem>
        <MenuItem key="download-omn-files" onClick={closeAndDispatch(showQuantitiesDialog('DownloadOmn', props.scope))}>
          OMN Files
        </MenuItem>
        <MenuItem key="download-bvn-files" onClick={closeAndDispatch(showQuantitiesDialog('DownloadBvn', props.scope))}>
          BVN Files (Hundegger)
        </MenuItem>
        {hasCybFeatureFlag ? (
          <MenuItem
            key="download-cyb-files"
            onClick={closeAndDispatch(showQuantitiesDialog('DownloadCyb', props.scope))}
          >
            CYB File
          </MenuItem>
        ) : null}
        {hasBladeFeatureFlag ? (
          <MenuItem
            key="download-blade-files"
            onClick={closeAndDispatch(showQuantitiesDialog('DownloadBlade', props.scope))}
          >
            Blade Files
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}
